import { cn } from '@/lib/utils'
import { HeroReveal, InteractiveDots, TextReveal } from '@/components/motion'

/**
 * Shell for the marketing form pages (contact, sign in) in the
 * reference contact layout: huge left-aligned two-line display title, grey
 * lede behind a hairline quote bar, then the form, all in one left-aligned
 * column over a faint, still blue halftone.
 */
export function FormPage({
  title,
  lede,
  children,
  className,
}: {
  title: string[]
  lede: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <section className={cn('relative overflow-hidden pt-16 pb-24 md:pt-24 md:pb-32', className)}>
      <InteractiveDots rgb="12,0,255" intensity={0.55} spacing={14} maxSize={3.2} />
      <div className="landing-container relative z-10">
        {/* The title takes a wider column than the form so it sets on two
            lines, as in the reference. */}
        <div className="mx-auto max-w-6xl">
          <TextReveal
            as="h1"
            lines={title}
            className="landing-text-hero-sm text-[var(--color-landing-gray-900)] dark:text-white"
          />
          <HeroReveal delay={0.2}>
            <div className="mt-10 max-w-md border-l border-[var(--color-landing-gray-300)] dark:border-[var(--color-landing-gray-700)] pl-5 text-[17px] leading-[1.45] tracking-[-0.02em] text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
              {lede}
            </div>
          </HeroReveal>
          <HeroReveal delay={0.3}>
            <div className="mt-16 max-w-5xl">{children}</div>
          </HeroReveal>
        </div>
      </div>
    </section>
  )
}

/** Visually hidden label + flat field. Placeholders carry the prompt. */
export function FormField({
  id,
  label,
  className,
  children,
}: {
  id: string
  label: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={className}>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      {children}
    </div>
  )
}

/** Small body-text line under a form, e.g. "Already have an account? Sign in". */
export function FormNote({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={cn('text-sm text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]', className)}>
      {children}
    </p>
  )
}

export const formLinkClass =
  'font-medium text-[var(--color-landing-gray-900)] dark:text-white underline underline-offset-4 decoration-[var(--color-landing-gray-300)] hover:decoration-[var(--color-landing-accent)] hover:text-[var(--color-landing-accent)] transition-colors'

/**
 * Full-width split for the auth pages: the form column on the left (title,
 * one-line lede, form), a full-height brand panel bleeding to the right
 * edge. No pointer effects: this is the most-used page and stays calm.
 */
export function AuthSplit({
  title,
  lede,
  children,
  aside,
  tone = 'blue',
}: {
  title: string
  lede: React.ReactNode
  children: React.ReactNode
  aside: React.ReactNode
  tone?: 'blue' | 'purple'
}) {
  return (
    <section className="grid min-h-[calc(100svh-72px)] lg:grid-cols-2">
      <div className="flex flex-col justify-center px-[clamp(1.5rem,6vw,7rem)] py-16 lg:py-20">
        <div className="w-full max-w-[32rem]">
          <h1 className="landing-text-hero text-[var(--color-landing-gray-900)] dark:text-white">{title}</h1>
          <div className="mt-6 max-w-sm text-[17px] leading-[1.45] tracking-[-0.02em] text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
            {lede}
          </div>
          <div className="mt-10">{children}</div>
        </div>
      </div>
      <aside
        className={cn(
          'relative overflow-hidden text-white',
          tone === 'blue' ? 'bg-[var(--color-landing-accent)]' : 'bg-[var(--color-landing-secondary)]'
        )}
      >
        <InteractiveDots spacing={16} maxSize={3.6} />
        <div className="relative z-10 flex h-full flex-col justify-between gap-12 px-[clamp(1.5rem,5vw,6rem)] py-16 lg:py-20">
          {aside}
        </div>
      </aside>
    </section>
  )
}
